import { ShapeType } from '@/types/commontypes';
import { isDefined } from '@react-financial-charts/core';
import { formatSmallPrice, SMALL_VALUE_THRESHOLD } from './duration';

interface LabelParams {
  showPriceRange?: boolean;
  showPercentChange?: boolean;
  showBarsRange?: boolean;
  showDateTimeRange?: boolean;
  showAngle?: boolean;
  priceRange?: number;
  totalPercentChange?: number;
  barsRange?: number;
  daysBetween?: {
    days?: number;
    hours?: number;
    minutes?: number;
  };
  angle?: number;
}
export const getCombinedTrendLabel = ({
  showPriceRange,
  showPercentChange,
  showBarsRange,
  showDateTimeRange,
  showAngle,
  priceRange,
  totalPercentChange,
  barsRange,
  daysBetween,
  angle,
}: LabelParams): string => {
  const lines: string[] = [];

  // ─── Line 1: Price + Percent ─────────────────────────────
  const pricePart = showPriceRange && isDefined(priceRange) ? formatSmallPrice(priceRange) : '';

  const percentPart = showPercentChange && isDefined(totalPercentChange) ? `(${totalPercentChange.toFixed(2)}%)` : '';

  if (pricePart || percentPart) {
    const formattedPrice = pricePart ? formatNumber(parseFloat(pricePart)) : '';
    lines.push([formattedPrice, percentPart].filter(Boolean).join(' '));
  }

  // ─── Line 2: Bars + Time ─────────────────────────────────
  const barPart = showBarsRange && isDefined(barsRange) ? `${formatNumber(parseFloat(barsRange.toString()))} bars` : '';

  const datePart = showDateTimeRange && daysBetween ? `(${formatDaysBetween(daysBetween)})` : '';

  if (barPart || datePart) {
    lines.push([barPart, datePart].filter(Boolean).join(' '));
  }

  // ─── Line 3: Angle ───────────────────────────────────────
  if (showAngle && isDefined(angle)) {
    lines.push(`Angle: ${angle.toFixed(2)}°`);
  }

  return lines.join('\n');
};

export const formatDaysBetween = (data: { days?: number; hours?: number; minutes?: number }) => {
  // Example input: "2d5h30m"
  const durationParts = data
    ? [
        data.days ? `${data.days}d` : null,
        data.hours ? `${data.hours}h` : null,
        data.minutes ? `${data.minutes}m` : null,
      ]
        .filter(Boolean)
        .join(' ')
    : '';

  return durationParts; // remove trailing space
};

export const getLabelText = (idx: number, type: string): string => {
  if (type === ShapeType.ELLIOTT_CORRECTION_ABC) {
    const labels = ['0', 'A', 'B', 'C'];
    return labels[idx] ?? idx.toString(); // Fallback if idx > 3
  }
  if (type === ShapeType.XABCD_PATTERN) {
    const label = ['X', 'A', 'B', 'C', 'D'];
    return label[idx] ?? idx.toString();
  }

  return idx.toString();
};

export const getLabelOffset = (y1?: number, y2?: number) => {
  if (!y1 || !y2) return 0;

  return y1 > SMALL_VALUE_THRESHOLD && y2 > SMALL_VALUE_THRESHOLD ? 0.7 : 0.0000002;
};

export const formatNumber = (value: number) =>
  new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
